import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Collapse, Container, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, InputAdornment, MenuItem, Paper, TextField, Tooltip, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StarIcon from "@mui/icons-material/Star";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import { useNotifier } from "../../../Core/Notifier";
import { API_URL } from "../../../config";

const T = {
  navy: "#0d1b2a", gold: "#c9a96e", cream: "#faf8f4", white: "#fff",
  bg: "#f7f5f1", slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  greenBg: "#edf7ee", greenTx: "#2d6a35", greenBd: "#a8d5ac",
  redTx: "#9b2c2c", redBg: "#fef2f2",
  peach: "#ffebd3", inputBg: "#f8f7f5",
};
const inputSx = {
  "& .MuiInputBase-root": { fontSize: 13.5, borderRadius: "10px", bgcolor: T.inputBg, border: `1px solid ${T.line}`, "&.Mui-focused": { bgcolor: T.peach, borderColor: T.gold }, transition: "all 0.15s", "& fieldset": { border: "none" } },
  "& .MuiInputLabel-root": { fontSize: 13 }, "& input": { py: "9px" },
};

const PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];
const PRICING_LABELS: Record<string, string> = { per_event: "Per Event", per_hour: "Per Hour", per_unit: "Per Unit", per_plate: "Per Plate" };

const hdrs = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` });
const post = (p: string, b: any) => fetch(`${API_URL}${p}`, { method: "POST", headers: hdrs(), body: JSON.stringify(b) }).then(r => r.json());
const get_ = (p: string) => fetch(`${API_URL}${p}`, { headers: hdrs() }).then(r => r.json());
const del_ = (p: string) => fetch(`${API_URL}${p}`, { method: "DELETE", headers: hdrs() }).then(r => r.json());

const fmtCAD = (n: number) => n.toLocaleString("en-CA", { style: "currency", currency: "CAD" });

interface VendorData {
  id?: number; name: string; company_name: string; phone: string; email: string;
  street_address: string; city: string; province: string; postal_code: string; notes: string;
  status: string; vendor_user_id?: number | null;
  item_prices?: any[];
}

const EMPTY: VendorData = {
  name: "", company_name: "", phone: "", email: "",
  street_address: "", city: "", province: "ON", postal_code: "", notes: "", status: "active",
};

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR FORM MODAL
   ═══════════════════════════════════════════════════════════════════════════ */
const VendorFormModal: React.FC<{
  open: boolean; vendor: VendorData;
  onSave: (v: VendorData) => void; onClose: () => void; saving: boolean;
}> = ({ open, vendor, onSave, onClose, saving }) => {
  const [form, setForm] = useState<VendorData>(vendor);
  useEffect(() => { setForm(vendor); }, [vendor]);

  const set = (f: keyof VendorData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [f]: e.target.value }));

  const isEdit = !!vendor.id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(201,169,110,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <StorefrontOutlinedIcon sx={{ fontSize: 16, color: T.gold }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>{isEdit ? "Edit Vendor" : "New Vendor"}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.slateLt, mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>Contact Information</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2.5 }}>
          <TextField label="Vendor Name *" value={form.name} onChange={set("name")} size="small" autoFocus sx={inputSx} />
          <TextField label="Company Name" value={form.company_name} onChange={set("company_name")} size="small" sx={inputSx} />
          <TextField label="Phone" value={form.phone} onChange={set("phone")} size="small" sx={inputSx}
            InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }} />
          <TextField label="Email" value={form.email} onChange={set("email")} size="small" sx={inputSx}
            InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }} />
        </Box>

        <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.slateLt, mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>Address</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2, mb: 2.5 }}>
          <TextField label="Street Address" value={form.street_address} onChange={set("street_address")} size="small" fullWidth sx={inputSx}
            InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 100px 100px", gap: 2 }}>
            <TextField label="City" value={form.city} onChange={set("city")} size="small" sx={inputSx} />
            <TextField label="Province" value={form.province} onChange={set("province")} size="small" select sx={inputSx}>
              {PROVINCES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
            <TextField label="Postal Code" value={form.postal_code} onChange={set("postal_code")} size="small" sx={inputSx} />
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: 2 }}>
          <TextField label="Notes" value={form.notes} onChange={set("notes")} size="small" multiline rows={2} sx={inputSx} />
          <TextField label="Status" value={form.status} onChange={set("status")} size="small" select sx={inputSx}>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Box>
      </DialogContent>

      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button variant="contained" onClick={() => { if (form.name.trim()) onSave(form); }} disabled={saving || !form.name.trim()}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" }, "&.Mui-disabled": { bgcolor: T.line } }}>
          {saving ? "Saving..." : "Save Vendor"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CREDENTIALS POPUP
   ═══════════════════════════════════════════════════════════════════════════ */
const CredentialsModal: React.FC<{
  open: boolean; vendor: VendorData | null;
  onClose: () => void; onCreated: () => void;
}> = ({ open, vendor, onClose, onCreated }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(false);

  useEffect(() => {
    setEmail(vendor?.email ?? "");
    setPassword(Math.random().toString(36).slice(-8) + "A1!");
    setCreated(false);
  }, [vendor]);

  const handleCreate = async () => {
    if (!vendor?.id) return;
    setSaving(true);
    const res = await post(`/banquet/vendors/${vendor.id}/credentials`, { email, password, name: vendor.name });
    if (res.code === 200) { showMessage("Credentials created"); setCreated(true); onCreated(); }
    else showErrorMessage(res.message);
    setSaving(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showMessage("Copied to clipboard");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <VpnKeyOutlinedIcon sx={{ fontSize: 16, color: T.gold }} />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.white }}>Vendor Portal Credentials</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>

      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        {created ? (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 40, color: T.greenTx, mb: 1 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.navy, mb: 2 }}>Credentials Created!</Typography>
            <Box sx={{ bgcolor: T.bg, borderRadius: "10px", p: 2, textAlign: "left" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ fontSize: 11, color: T.slateLt }}>Portal URL</Typography>
                <IconButton size="small" onClick={() => copyToClipboard(`${window.location.origin}/vendor/login`)}><ContentCopyIcon sx={{ fontSize: 12 }} /></IconButton>
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy, mb: 1.5 }}>{window.location.origin}/vendor/login</Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, color: T.slateLt }}>Email</Typography>
                <IconButton size="small" onClick={() => copyToClipboard(email)}><ContentCopyIcon sx={{ fontSize: 12 }} /></IconButton>
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy, mb: 1.5 }}>{email}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, color: T.slateLt }}>Password</Typography>
                <IconButton size="small" onClick={() => copyToClipboard(password)}><ContentCopyIcon sx={{ fontSize: 12 }} /></IconButton>
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy, fontFamily: "monospace" }}>{password}</Typography>
            </Box>
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: 12, color: T.slateLt }}>
              Set up portal login for <strong>{vendor?.name}</strong>. They can access their events, pricing, and notifications.
            </Typography>
            <TextField label="Login Email" value={email} onChange={e => setEmail(e.target.value)} size="small" sx={inputSx}
              InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }} />
            <TextField label="Password" value={password} onChange={e => setPassword(e.target.value)} size="small" sx={inputSx}
              InputProps={{ startAdornment: <InputAdornment position="start"><VpnKeyOutlinedIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }} />
          </>
        )}
      </DialogContent>

      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        {created ? (
          <Button variant="contained" onClick={onClose}
            sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", "&:hover": { bgcolor: "#1a3550" } }}>Done</Button>
        ) : (
          <>
            <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Skip</Button>
            <Button variant="contained" onClick={handleCreate} disabled={saving || !email.trim() || !password.trim()}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <VpnKeyOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
              {saving ? "Creating..." : "Create Credentials"}
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
const BanquetVendors: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [vendorDetail, setVendorDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [vendorModal, setVendorModal] = useState(false);
  const [editVendor, setEditVendor] = useState<VendorData>({ ...EMPTY });
  const [credModal, setCredModal] = useState<VendorData | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    const res = await get_("/banquet/vendors");
    if (res.code === 200) setVendors(res.data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const filtered = useMemo(() =>
    vendors.filter(v => !search || v.name.toLowerCase().includes(search.toLowerCase()) || (v.company_name || "").toLowerCase().includes(search.toLowerCase())),
    [vendors, search]);

  const handleSave = async (vendor: VendorData) => {
    setSaving(true);
    const res = await post("/banquet/vendors", vendor);
    if (res.code === 200) {
      showMessage(res.message);
      setVendorModal(false);
      fetchVendors();
      if (!vendor.id && res.data?.id) {
        setCredModal({ ...vendor, id: res.data.id });
      }
    } else showErrorMessage(res.message);
    setSaving(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const res = await del_(`/banquet/vendors/${deleteTarget.id}`);
    if (res.code === 200) { showMessage(res.message); fetchVendors(); }
    else showErrorMessage(res.message);
    setIsDeleting(false);
    setDeleteTarget(null);
  };

  const toggleExpand = async (vendorId: number) => {
    if (expandedId === vendorId) {
      setExpandedId(null);
      setVendorDetail(null);
      return;
    }
    setExpandedId(vendorId);
    setDetailLoading(true);
    const res = await get_(`/banquet/vendors/${vendorId}`);
    if (res.code === 200) setVendorDetail(res.data);
    setDetailLoading(false);
  };

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 }, pb: 6 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, pt: 1 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: T.navy, letterSpacing: "-0.02em" }}>Vendors</Typography>
          <Typography sx={{ fontSize: 13, color: T.slateLt, mt: 0.3 }}>Manage vendor profiles, assigned items, and portal access</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setEditVendor({ ...EMPTY }); setVendorModal(true); }}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 2.5, boxShadow: "none", "&:hover": { bgcolor: "#1a3550" } }}>
          Add Vendor
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2.5 }}>
        <TextField size="small" placeholder="Search vendors..." value={search} onChange={e => setSearch(e.target.value)} fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: T.slateLt }} /></InputAdornment> }}
          sx={{ ...inputSx, maxWidth: 360 }} />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: T.gold }} /></Box>
      ) : filtered.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: "14px", border: `1px solid ${T.line}` }}>
          <StorefrontOutlinedIcon sx={{ fontSize: 40, color: T.slateLt, mb: 1 }} />
          <Typography sx={{ fontSize: 14, color: T.slateLt }}>No vendors found.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {filtered.map((v: any) => {
            const hasCredentials = !!v.vendor_user_id;
            const itemCount = v.item_prices?.length ?? 0;
            const isExpanded = expandedId === v.id;
            return (
              <Paper key={v.id} elevation={0} sx={{ borderRadius: "12px", border: isExpanded ? `1.5px solid ${T.gold}40` : "1.5px solid transparent", bgcolor: T.white, boxShadow: "0 1px 2px rgba(0,0,0,0.02)", overflow: "hidden", transition: "all 0.15s" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2, cursor: "pointer", "&:hover": { bgcolor: "#fef6ee" } }} onClick={() => toggleExpand(v.id)}>
                  <Box sx={{ width: 48, height: 48, borderRadius: "12px", flexShrink: 0, bgcolor: `${T.gold}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <StorefrontOutlinedIcon sx={{ fontSize: 22, color: T.gold }} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.3 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.navy }} noWrap>{v.name}</Typography>
                      <Chip label={v.status} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: v.status === "active" ? T.greenBg : T.redBg, color: v.status === "active" ? T.greenTx : T.redTx }} />
                      {hasCredentials && (
                        <Tooltip title="Has portal access"><Chip icon={<VpnKeyOutlinedIcon sx={{ fontSize: 10, color: `${T.greenTx} !important` }} />} label="Portal" size="small" sx={{ height: 18, fontSize: 9, bgcolor: T.greenBg, color: T.greenTx }} /></Tooltip>
                      )}
                      {itemCount > 0 && (
                        <Chip icon={<InventoryOutlinedIcon sx={{ fontSize: 10, color: `${T.gold} !important` }} />} label={`${itemCount} items`} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: `${T.gold}12`, color: T.gold }} />
                      )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                      {v.company_name && <Typography sx={{ fontSize: 11, color: T.slateLt, display: "flex", alignItems: "center", gap: 0.3 }}><BusinessOutlinedIcon sx={{ fontSize: 11 }} /> {v.company_name}</Typography>}
                      {v.phone && <Typography sx={{ fontSize: 11, color: T.slateLt, display: "flex", alignItems: "center", gap: 0.3 }}><PhoneOutlinedIcon sx={{ fontSize: 11 }} /> {v.phone}</Typography>}
                      {v.email && <Typography sx={{ fontSize: 11, color: T.slateLt, display: "flex", alignItems: "center", gap: 0.3 }}><EmailOutlinedIcon sx={{ fontSize: 11 }} /> {v.email}</Typography>}
                      {v.city && <Typography sx={{ fontSize: 11, color: T.slateLt, display: "flex", alignItems: "center", gap: 0.3 }}><LocationOnOutlinedIcon sx={{ fontSize: 11 }} /> {v.city}, {v.province}</Typography>}
                    </Box>
                  </Box>

                  {!hasCredentials && (
                    <Tooltip title="Set up portal credentials">
                      <Button size="small" startIcon={<VpnKeyOutlinedIcon sx={{ fontSize: 13 }} />}
                        onClick={e => { e.stopPropagation(); setCredModal(v); }}
                        sx={{ textTransform: "none", fontSize: 10, fontWeight: 600, color: T.gold, minWidth: 0 }}>
                        Credentials
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title="Edit"><IconButton size="small" onClick={e => { e.stopPropagation(); setEditVendor(v); setVendorModal(true); }} sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}><EditOutlinedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" onClick={e => { e.stopPropagation(); setDeleteTarget({ id: v.id, name: v.name }); }} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                  {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18, color: T.slateLt }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: T.slateLt }} />}
                </Box>

                {/* Expanded: assigned items */}
                <Collapse in={isExpanded}>
                  <Box sx={{ px: 2, pb: 2, borderTop: `1px solid ${T.line}` }}>
                    {detailLoading ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={20} sx={{ color: T.gold }} /></Box>
                    ) : vendorDetail?.item_prices?.length > 0 ? (
                      <>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.slateLt, mt: 1.5, mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>Assigned Items & Pricing</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          {vendorDetail.item_prices.map((ip: any) => (
                            <Box key={ip.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.6, px: 1, borderRadius: "8px", "&:hover": { bgcolor: T.bg } }}>
                              <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: ip.is_default ? T.gold : T.slateLt, flexShrink: 0 }} />
                              <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.navy, flex: 1 }}>
                                {ip.item?.name ?? "Unknown Item"}
                              </Typography>
                              {ip.item?.service && (
                                <Chip label={ip.item.service.name} size="small" sx={{ height: 16, fontSize: 8, bgcolor: T.bg, color: T.slate }} />
                              )}
                              {ip.item?.category && (
                                <Chip label={ip.item.category.name} size="small" sx={{ height: 16, fontSize: 8, bgcolor: T.bg, color: T.slate }} />
                              )}
                              <Chip label={PRICING_LABELS[ip.pricing_type] || ip.pricing_type} size="small" sx={{ height: 16, fontSize: 8, bgcolor: `${T.gold}10`, color: T.gold }} />
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.greenTx, minWidth: 70, textAlign: "right" }}>{fmtCAD(Number(ip.price))}</Typography>
                              {ip.is_default && (
                                <Tooltip title="Default vendor for this item"><StarIcon sx={{ fontSize: 13, color: T.gold }} /></Tooltip>
                              )}
                              {ip.is_adjustable && (
                                <Chip label="Adj" size="small" sx={{ height: 14, fontSize: 7, bgcolor: T.peach, color: T.gold }} />
                              )}
                            </Box>
                          ))}
                        </Box>
                      </>
                    ) : (
                      <Typography sx={{ fontSize: 12, color: T.slateLt, py: 2, textAlign: "center" }}>
                        No items assigned yet. Use the Service Catalogue to assign items to this vendor.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}

      <VendorFormModal open={vendorModal} vendor={editVendor} onSave={handleSave} onClose={() => setVendorModal(false)} saving={saving} />
      <CredentialsModal open={!!credModal} vendor={credModal} onClose={() => setCredModal(null)} onCreated={fetchVendors} />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}><WarningAmberIcon color="error" /> Delete vendor?</DialogTitle>
        <DialogContent><DialogContentText sx={{ fontSize: 13 }}>Delete <strong>{deleteTarget?.name}</strong>?</DialogContentText></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={isDeleting} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>{isDeleting ? "..." : "Delete"}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BanquetVendors;
